/**
 * getStoreFromContext
 *
 * @internal - Validates and retrieves state/persist from context by name.
 * Throws helpful errors if context is null, store not found, or type mismatch.
 */

import type { StoresContextValue, DocOrCollectionState, DocOrCollectionPersist, PersistType } from '../Store.types';

/**
 * Validate context and get state/persist by name.
 * Optionally validates the store type matches the expected type.
 * Throws helpful errors for debugging.
 *
 * @param ctx - The stores context value
 * @param name - The store name to retrieve
 * @param expectedType - Expected persist type ('document' | 'collection' | 'any')
 */
export function getStoreFromContext(
  ctx: StoresContextValue | null,
  name: string,
  expectedType: PersistType
): { state: DocOrCollectionState; persist: DocOrCollectionPersist } {
  if (!ctx) {
    throw new Error(
      `[Data Store] Hook called outside of StoreProvider.\n\n` +
      `Wrap your component tree with <StoreProvider>.`
    );
  }

  const state = ctx.states[name];
  const persist = ctx.persists[name];

  if (!state || !persist) {
    const availableNames = Object.keys(ctx.states).join(', ') || '(none)';
    throw new Error(
      `[Data Store] Store "${name}" not found.\n\n` +
      `Available stores: ${availableNames}`
    );
  }

  if (expectedType !== 'any' && persist.persistType !== expectedType) {
    const actualType = persist.persistType;
    const suggestedHook = actualType === 'document' ? 'useStoreDocument' : 'useStoreCollection';
    throw new Error(
      `[Data Store] Type mismatch for store "${name}".\n\n` +
      `Expected: ${expectedType}, Actual: ${actualType}\n` +
      `Use ${suggestedHook} instead.`
    );
  }

  return { state, persist };
}
